import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, InputNumber, message, Space, Row, Col, Typography, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, DownloadOutlined } from '@ant-design/icons';
import api from '../utils/api';
import CompanySelect from '../components/CompanySelect';
import { downloadCsv, yearOptions } from '../utils/exportCsv';
import { useDeepLinkWhen } from '../utils/deeplink';

const { Title, Text } = Typography;
const { Option } = Select;

const categoryLabels = {
  youtube_revenue: 'YouTube Revenue',
  distribution_royalty: 'Distribution Royalty',
  other_income: 'Other Income'
};

const categoryColors = {
  youtube_revenue: 'red',
  distribution_royalty: 'blue',
  other_income: 'green'
};

export default function Income() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  // AI answers open this page on the asked month (?month=YYYY-MM).
  const when = useDeepLinkWhen();
  const [filters, setFilters] = useState({
    company_id: '', category: '',
    month: when.month ? when.month.month() + 1 : new Date().getMonth() + 1,
    year: when.month ? when.month.year() : new Date().getFullYear(),
    page: 1
  });
  const [form] = Form.useForm();

  useEffect(() => { fetchCompanies(); }, []);
  useEffect(() => { fetchRecords(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await api.get('/finance/income', { params: filters });
      setRecords(res.data.records);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async (values) => {
    try {
      if (editing) {
        await api.put(`/finance/income/${editing.id}`, values);
        message.success('Updated!');
      } else {
        await api.post('/finance/income', values);
        message.success('Income added!');
      }
      setModalVisible(false);
      setEditing(null);
      form.resetFields();
      fetchRecords();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/finance/income/${id}`); message.success('Deleted'); fetchRecords(); }
    catch (err) { message.error('Failed'); }
  };

  const totalAmount = records.reduce((s, r) => s + r.amount, 0);

  const columns = [
    { title: 'Company', dataIndex: 'company_name', key: 'company' },
    { title: 'Category', dataIndex: 'category', key: 'cat', render: (c) => <Tag color={categoryColors[c]}>{categoryLabels[c]}</Tag> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v, r) => (
      <div>
        <Text strong style={{ color: '#52c41a' }}>₹{Number(v || 0).toLocaleString('en-IN')}</Text>
        {Number(r.amount_usd) > 0 && <><br /><Text style={{ color: '#1677ff', fontSize: 12 }}>${Number(r.amount_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></>}
      </div>
    ) },
    { title: 'Description', dataIndex: 'description', key: 'desc', ellipsis: true },
    { title: 'Song', dataIndex: 'song_title', key: 'song', render: (v) => v || '-' },
    { title: 'Channel', dataIndex: 'channel_name', key: 'channel', render: (v) => v || '-' },
    {
      title: 'Actions', key: 'actions', width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); form.setFieldsValue(r); setModalVisible(true); }} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><DollarOutlined style={{ color: '#52c41a' }} /> Income</Title>
          <Text type="secondary">Total: <Text strong style={{ color: '#52c41a' }}>₹{totalAmount.toLocaleString('en-IN')}</Text></Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/finance/income/export', { company_id: filters.company_id, category: filters.category, month: filters.month, year: filters.year })}>Download Excel</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); form.setFieldsValue({ month: filters.month, year: filters.year }); setModalVisible(true); }}>Add Income</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Select placeholder="Company" value={filters.company_id || undefined} onChange={(v) => setFilters({ ...filters, company_id: v || '' })} allowClear style={{ width: '100%' }}>
            {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <Select placeholder="Category" value={filters.category || undefined} onChange={(v) => setFilters({ ...filters, category: v || '' })} allowClear style={{ width: '100%' }}>
            <Option value="youtube_revenue">YouTube Revenue</Option>
            <Option value="distribution_royalty">Distribution Royalty</Option>
            <Option value="other_income">Other Income</Option>
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <Select value={filters.month} onChange={(v) => setFilters({ ...filters, month: v })} style={{ width: '100%' }}>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => <Option key={i+1} value={i+1}>{m}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <Select value={filters.year} onChange={(v) => setFilters({ ...filters, year: v })} style={{ width: '100%' }}>
            {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
          </Select>
        </Col>
      </Row>

      <Table dataSource={records} columns={columns} rowKey="id" loading={loading}
        pagination={{ current: filters.page, total, pageSize: 50, onChange: (p) => setFilters({ ...filters, page: p }) }} />

      <Modal title={editing ? 'Edit Income' : 'Add Income'} open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditing(null); }} onOk={() => form.submit()} width={600}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="company_id" label="Company" rules={[{ required: true }]}>
                <CompanySelect companies={companies} onCompaniesChange={fetchCompanies} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select>
                  <Option value="youtube_revenue">YouTube Revenue</Option>
                  <Option value="distribution_royalty">Distribution Royalty</Option>
                  <Option value="other_income">Other Income</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="date" label="Date"><Input type="date" /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="month" label="Month"><InputNumber min={1} max={12} style={{ width: '100%' }} /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="year" label="Year"><InputNumber min={2020} max={2030} style={{ width: '100%' }} /></Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
